import { FC, useEffect, useState } from "react";
import { useDataTableStore } from "../../../stores/DataTableStore";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useIntl } from "react-intl";
import { useSettingsStore } from "../../../stores/SettingsStore";
import { handleCapsSelect } from "../../../functions/general";
import CloseIcon from '@mui/icons-material/Close';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import VirtualizedSelect, { VirtualizedSelectOption } from "../../../components/Select/SelectVirtualized";
import { CRMColumnDef } from "../../../components/DataTable/MasterDataTableTanstack";
import { DataTableSelect } from "../../../components/DataTable/DataTableSelect";
import { usePipelineStore } from "../../../stores/PipelineStore";
import { SettingMultiPipelines } from "../../../interfaces/Settings";


interface FilterProgressesProps {
    tableKey: string
    manageColumns: any
    setManageColumns: any
    onVisibilityChange?: (selectedColumns: string[]) => void
}

const FilterProgresses: FC<FilterProgressesProps> = ({ tableKey, manageColumns, setManageColumns, onVisibilityChange }) => {
    const intl = useIntl()
    const { settings } = useSettingsStore()

    const { tablesData, setTableColumnVisibility, setSelectColumn, selectedColumns, setSelectedFilter,  setMasterDateRange,setFilteredTablesData, } = useDataTableStore();
    const { multiPipelines } = usePipelineStore()
    
    const [isFiltering, setIsFiltering] = useState<boolean>(false)
    const [columnsData, setColumnsData] = useState<VirtualizedSelectOption[]>([])
    const [templatePipelineOptions, setTemplatePipelineOptions] = useState<VirtualizedSelectOption[]>([])
    
    
    const showAllColumnIds = manageColumns.map((column: ColumnDef<any>) => column.id)
    const excludedColumnExcel = manageColumns.map((column: ColumnDef<any>) => 
        column.header === intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ACTION' }) || column.id === 'action' ? 'action' : ''
    )

     useEffect(() => {
            let data = multiPipelines.map((pipeline: SettingMultiPipelines) => {
                return {
                    value: pipeline.id,
                    label: pipeline.name
                }
            })
    
            setTemplatePipelineOptions([...data])
        }, [multiPipelines])


        const selectConfigs = [
      {
            placeholder: `${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} ${intl.formatMessage({ id: "FORM.LABEL.TEMPLATE" }, { title: settings.pipeline_title })}`,
            tableKey: tableKey,
            filterKey: 'database_pipeline',
            options: templatePipelineOptions,
            onChange: (values: string[]) => {
                setSelectedFilter(tableKey, 'template_pipeline', values)
                setIsFiltering(true)
                }
        },
    ]

    useEffect(() => {
        const removeErrorClass = () => {
            const errorPickers = document.querySelectorAll('.rs-picker-error');

            errorPickers.forEach(picker => {
                picker.classList.remove('rs-picker-error');
            });
        };

        removeErrorClass();

        const intervalId = setInterval(removeErrorClass, 100);

        const handlePickerOpen = () => {
            setTimeout(removeErrorClass, 10);
        };

        document.addEventListener('click', handlePickerOpen);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('click', handlePickerOpen);
        };
    }, []);

    useEffect(() => {
        // console.log('manageColumns',manageColumns)
        const columnsSelectedData = manageColumns.map((column: any) => ({
            value: column.id,
            label: column.header,
        }))
        setColumnsData(columnsSelectedData)
    }, [manageColumns])

    const selectColumnsConfigs = {
        placeholder: `${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} ${intl.formatMessage({ id: "FORM.LABEL.COLUMN" })}`,
        tableKey,
        filterKey: `column_${tableKey}`,
        options: columnsData,
    }

    // --------------------------------------- Reset Data ---------------------------------------
    const handleResetColumnVisibility = () => {
        setTableColumnVisibility(
            tableKey,
            showAllColumnIds.reduce((acc: Record<string, boolean>, columnId: string) => {
              acc[columnId] = true; // Set semua kolom yang ada jadi 'visible' (true)
              return acc;
            }, {} as Record<string, boolean>) // Menentukan tipe eksplisit untuk `acc`
        );
    }
    const handleResetFilter = () => {
        setIsFiltering(false) // reset table is filtering btn
        setSelectColumn(tableKey, []) // reset selected column
        setFilteredTablesData(tableKey, tablesData[tableKey]) // reset to original data table 
        setSelectedFilter(tableKey, 'database_pipeline', [])
        setMasterDateRange({
            invoices: null,
        })

        handleResetColumnVisibility()          
        // onVisibilityChange(showAllColumnIds) // reset show all column
    }

    useEffect(() => {
        handleResetFilter()
    }, [tableKey]);

    // --------------------------------------- Filter Column Visibility ---------------------------------------
    const handleColumnChange = (selectedOptions: VirtualizedSelectOption[]) => {
        if (manageColumns && setManageColumns) {
            if (selectedOptions.length === 0) {
                setManageColumns(manageColumns.map((column: CRMColumnDef<any>) => ({ // set excel export/import column to hide
                    ...column,
                    excelColumnHidden: column.id ? excludedColumnExcel.includes(column.id) : false,
                })))
                handleResetColumnVisibility()          
                setIsFiltering(false)
            } else {
                const selectedColumnIds = selectedOptions.map((option) => option.value);
                setManageColumns(manageColumns.map((column: CRMColumnDef<any>) => ({
                    ...column,
                    excelColumnHidden: column.id ? !selectedColumnIds.includes(column.id) && excludedColumnExcel.includes(column.id) : false,
                })))

                setTableColumnVisibility(tableKey, selectedColumnIds.reduce<VisibilityState>((acc, id) => {
                    acc[id] = true;
                    return acc;
                }, {}))
                setIsFiltering(true)
            }

            setSelectColumn(tableKey, selectedOptions)
        }
    }

    useEffect(() => {
        setSelectColumn(tableKey, [])
    }, [])


    

    return (
        <>
            {isFiltering
                ?
                <div className="d-flex gap-2">
                    <button
                        id="drawer_filter_merchant"
                        className="btn" 
                        style={{ backgroundColor: "#f1faff", border: "2px solid #009EF7" }}
                    >
                        <FilterAltIcon />
                    </button>
                    <button type="button"
                        className="btn btn-white text-primary"
                        onClick={() => { handleResetFilter() }}
                    >
                        Reset Filter
                    </button>
                </div>
                :
                <button
                    id="drawer_filter_merchant"
                    type="button"
                    className="btn btn-light"
                >
                    <FilterAltIcon />
                </button>
            }

            <div
                id="kt_drawer_filter_merchant"
                className="bg-white"
                data-kt-drawer="true"
                data-kt-drawer-activate="true"
                data-kt-drawer-overlay="true"
                data-kt-drawer-toggle="#drawer_filter_merchant"
                data-kt-drawer-close="#kt_drawer_example_basic_close"
                data-kt-drawer-width="{default:'300px', 'md': '600px'}"
            >
                <div className='card w-100 rounded-0'>
                    <div className='card-header pe-5'>
                        <div className='card-title'>
                            <div className='d-flex justify-content-center flex-column me-3'>
                                <span className='fs-4 fw-bolder text-gray-900 text-hover-primary me-1 lh-1'>
                                    Filter {intl.formatMessage({ id: 'MENU.INVOICES' })}
                                </span>
                            </div>
                        </div>
                        <div className='card-toolbar'>
                            <div
                                className='btn btn-sm btn-icon btn-active-light-primary'
                                id='kt_drawer_example_basic_close'
                            >
                                <CloseIcon fontSize='small' />
                            </div>
                        </div>
                    </div>
                    <div className='card-body' style={{overflowY: 'auto'}}>
                        <div>
                            <div style={{ fontWeight: "600" }}>{intl.formatMessage({ id: "FORM.LABEL.MANAGE_COLUMN" })}</div>
                            <div className="separator separator-dashed my-3"></div>
                            <div className='p-1'>
                                <VirtualizedSelect
                                    closeMenuOnSelect={false}
                                    placeholder={selectColumnsConfigs.placeholder}
                                    isMulti={true}
                                    value={selectedColumns[tableKey]}
                                    onChange={(selectedOptions) => handleColumnChange(selectedOptions)}
                                    options={selectColumnsConfigs.options}
                                    onInputChange={handleCapsSelect}
                                />
                            </div>
                        </div>
                            {
                            selectConfigs.map((config, index) => (
                                <DataTableSelect
                                    placeholder={config.placeholder}
                                    tableKey={config.tableKey}
                                    filterKey={config.filterKey}
                                    key={index}
                                    options={config.options}
                                    onChange={config.onChange}

                                />
                            ))
                        }
                    </div>
                </div>
            </div>
        </>
    )
    
}

export {FilterProgresses}